import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { issueNoteService } from '../../services/issueNoteService';
import { apiService } from '../../services/api';
import { CreateIssueNoteRequest } from '../../types';
import './IssueNotePage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save, Plus, X, AlertCircle } from 'lucide-react';

interface WarehouseOption { id: number; name: string; }
interface ProductOption   { id: number; name: string; sku: string; }
interface UnitOption      { id: number; unitName: string; }

interface LineItem {
  productId: number;
  unitId:    number;
  quantity:  number;
  purpose:   string;
  notes:     string;
}

const emptyLine = (): LineItem => ({
  productId: 0,
  unitId:    0,
  quantity:  0,
  purpose:   '',
  notes:     '',
});

const IssueNoteCreatePage: React.FC = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id?: string }>();
  const { t }     = useTranslation();
  const { user }  = useAuth();
  const isEdit    = !!id;

  const [warehouseId, setWarehouseId] = useState<number>(0);
  const [issueDate,   setIssueDate]   = useState(new Date().toISOString().split('T')[0]);
  const [notes,       setNotes]       = useState('');
  const [items,       setItems]       = useState<LineItem[]>([emptyLine()]);

  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [products,   setProducts]   = useState<ProductOption[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);

  const [loading,     setLoading]     = useState(false);
  const [initialLoad, setInitialLoad] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        // Warehouses live under api/v1/organization/warehouses — the short
        // /warehouses path 404s. Products and units use api/v1/[controller],
        // so their short paths resolve correctly.
        const [whRes, prodRes, unitRes] = await Promise.all([
          apiService.get<unknown>('/organization/warehouses', { pageSize: 200 }),
          apiService.get<unknown>('/products',    { pageSize: 1000, isActive: true }),
          apiService.get<unknown>('/units',       { pageSize: 200 }),
        ]);

        const mapWh = (d: unknown): WarehouseOption[] => {
          const list = (d as any)?.data?.data ?? (d as any)?.data ?? [];
          return (list as any[]).map((w) => ({ id: Number(w.id ?? w.warehouseId), name: String(w.name ?? w.warehouseName) }));
        };
        const mapProd = (d: unknown): ProductOption[] => {
          const list = (d as any)?.data?.data ?? (d as any)?.data ?? [];
          return (list as any[]).map((p) => ({ id: Number(p.id ?? p.productId), name: String(p.name ?? p.productName), sku: String(p.sku ?? p.productCode ?? '') }));
        };
        const mapUnit = (d: unknown): UnitOption[] => {
          const list = (d as any)?.data?.data ?? (d as any)?.data ?? [];
          return (list as any[]).map((u) => ({ id: Number(u.id ?? u.unitId), unitName: String(u.unitName ?? u.name) }));
        };

        setWarehouses(mapWh(whRes.data));
        setProducts(mapProd(prodRes.data));
        setUnits(mapUnit(unitRes.data));

        if (isEdit && id) {
          const res = await issueNoteService.getById(id);
          const isn = res.data?.data;
          if (isn) {
            setWarehouseId(isn.warehouseId);
            setIssueDate(isn.issueDate.split('T')[0]);
            setNotes(isn.notes ?? '');
            if (isn.items?.length) {
              setItems(isn.items.map((i) => ({
                productId: i.productId,
                unitId:    i.unitId,
                quantity:  i.quantity,
                purpose:   i.purpose ?? '',
                notes:     i.notes ?? '',
              })));
            }
          }
        }
      } catch {
        setError(t('issueNote.failedToLoad', 'Failed to load data.'));
      } finally {
        setInitialLoad(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemChange = (index: number, field: keyof LineItem, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      (next[index] as any)[field] = value;
      return next;
    });
  };

  const addLine    = () => setItems((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const validItems = items.filter((i) => i.productId > 0 && i.unitId > 0 && i.quantity > 0);

  const validate = (): string | null => {
    if (warehouseId <= 0)       return t('issueNote.warehouseRequired', 'Warehouse is required.');
    if (!issueDate)             return t('issueNote.dateRequired', 'Issue date is required.');
    if (validItems.length === 0) return t('issueNote.itemsRequired', 'At least one valid item is required.');
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ve = validate();
    if (ve) { setError(ve); return; }
    setLoading(true);
    setError(null);

    try {
      if (isEdit && id) {
        await issueNoteService.update(id, {
          warehouseId,
          issueDate,
          notes: notes || undefined,
          issuedByUserId: user?.id ?? 0,
          items: validItems.map((i) => ({ productId: i.productId, unitId: i.unitId, quantity: i.quantity, purpose: i.purpose || undefined, notes: i.notes || undefined })),
        });
        navigate(`/issue-notes/${id}`, { state: { message: t('issueNote.updatedSuccess', 'Issue note updated successfully.') } });
      } else {
        const payload: CreateIssueNoteRequest = {
          warehouseId,
          issueDate,
          notes: notes || undefined,
          issuedByUserId: user?.id ?? 0,
          items: validItems.map((i) => ({ productId: i.productId, unitId: i.unitId, quantity: i.quantity, purpose: i.purpose || undefined, notes: i.notes || undefined })),
        };
        const res     = await issueNoteService.create(payload);
        const created = res.data?.data;
        navigate(`/issue-notes/${created?.id}`, { state: { message: t('issueNote.createdSuccess', 'Issue note {{number}} created.', { number: created?.issueNoteNumber }) } });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, isEdit ? t('issueNote.failedToUpdate', 'Failed to update.') : t('issueNote.failedToCreate', 'Failed to create.')).message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <Layout>
        <div className="isn-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <div className="ims-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="isn-create-page">

        {/* Top bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/issue-notes')}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('issueNote.backToList', 'Back to List')}
            </button>
            <div>
              <h1 className="ims-page-title">
                {isEdit ? t('issueNote.editTitle', 'Edit Issue Note') : t('issueNote.createTitle', 'Create Issue Note')}
              </h1>
              <p className="ims-page-subtitle">
                {isEdit ? t('issueNote.editSubtitle', 'Update issue note details') : t('issueNote.createSubtitle', 'Record internal stock issuance')}
              </p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="btn-cancel" onClick={() => navigate('/issue-notes')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {isEdit ? t('common.saveChanges') : t('issueNote.createIssueNote', 'Create Issue Note')}
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        <div className="ims-create-body">

          {/* LEFT — Issue Note Details */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('issueNote.issueNoteDetails', 'Issue Note Details')}</h2>
              </div>
              <div className="ims-card-body">

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('issueNote.warehouse', 'Warehouse')} <span className="required">*</span>
                  </label>
                  <select className="ims-form-control" value={warehouseId} onChange={(e) => setWarehouseId(Number(e.target.value))}>
                    <option value={0}>{t('issueNote.selectWarehouse', 'Select warehouse…')}</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('issueNote.issueDate', 'Issue Date')} <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    className="ims-form-control"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    required
                  />
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('common.notes', 'Notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('issueNote.notesPlaceholder', 'Add any relevant notes…')}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CENTER — Items */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('issueNote.issuedItems', 'Issued Items')}</h2>
                <button type="button" className="ims-btn ims-btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={addLine}>
                  <Plus size={12} strokeWidth={2.5} />
                  {t('issueNote.addItem', 'Add Item')}
                </button>
              </div>
              <div className="ims-lines-body">
                {items.length === 0 ? (
                  <div className="ims-items-empty">
                    <div className="ims-items-empty-icon">📦</div>
                    {t('issueNote.addItemsHint', 'Add items to issue from stock')}
                  </div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 200 }}>{t('issueNote.product', 'Product')}</th>
                          <th className="ims-th" style={{ minWidth: 120 }}>{t('issueNote.unit', 'Unit')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 100 }}>{t('issueNote.quantity', 'Quantity')}</th>
                          <th className="ims-th" style={{ minWidth: 200 }}>{t('issueNote.purpose', 'Purpose')}</th>
                          <th className="ims-th" style={{ minWidth: 180 }}>{t('issueNote.itemNotes', 'Notes')}</th>
                          <th className="ims-th ims-th--del" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index} className="ims-tr">
                            <td className="ims-td">
                              <select className="ims-cell-input" value={item.productId} onChange={(e) => handleItemChange(index, 'productId', Number(e.target.value))}>
                                <option value={0}>{t('issueNote.selectProduct', 'Select product…')}</option>
                                {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                              </select>
                            </td>
                            <td className="ims-td">
                              <select className="ims-cell-input" value={item.unitId} onChange={(e) => handleItemChange(index, 'unitId', Number(e.target.value))}>
                                <option value={0}>{t('issueNote.selectUnit', 'Select unit…')}</option>
                                {units.map((u) => <option key={u.id} value={u.id}>{u.unitName}</option>)}
                              </select>
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className="ims-cell-input ims-num-input"
                                step="0.01" min={0.01}
                                value={item.quantity || ''}
                                onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                              />
                            </td>
                            <td className="ims-td">
                              <input
                                type="text"
                                className="ims-cell-input"
                                value={item.purpose}
                                onChange={(e) => handleItemChange(index, 'purpose', e.target.value)}
                                placeholder={t('issueNote.purposePlaceholder', 'e.g. Kitchen use, Event supply…')}
                              />
                            </td>
                            <td className="ims-td">
                              <input
                                type="text"
                                className="ims-cell-input"
                                value={item.notes}
                                onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                placeholder={t('issueNote.notesPlaceholder', 'Optional notes…')}
                              />
                            </td>
                            <td className="ims-td ims-td--del">
                              <button type="button" className="ims-remove-btn" onClick={() => removeLine(index)} disabled={items.length === 1} title={t('common.remove')}>
                                <X size={12} strokeWidth={2.5} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — Summary */}
          <div className="ims-panel-right">

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('issueNote.issuedBy', 'Issued By')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-user-row">
                  <div className="ims-user-avatar">{user?.firstName?.[0]?.toUpperCase() ?? '?'}</div>
                  <div>
                    <div className="ims-user-name">{user?.firstName} {user?.lastName}</div>
                    <div className="ims-user-email">{user?.email}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('issueNote.summary', 'Summary')}</h3>
              </div>
              <div className="ims-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--ims-text-sub)' }}>{t('issueNote.totalItems', 'Total Items')}</span>
                    <span style={{ fontWeight: 700, color: 'var(--ims-text-main)' }}>{validItems.length}</span>
                  </div>
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--ims-raw-purple-100)', border: '1px solid var(--ims-raw-purple-200)' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--ims-raw-purple-800)' }}>
                      {t('issueNote.issuanceNote', 'Stock will be deducted on posting')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('issueNote.tipsTitle', 'Tips')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('issueNote.tip1', 'Select the warehouse from which stock will be issued.')}</li>
                  <li>{t('issueNote.tip2', 'Add a purpose to describe why this stock is being issued.')}</li>
                  <li>{t('issueNote.tip3', 'Submit for approval before posting to inventory.')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default IssueNoteCreatePage;
